import React from "react";
import moment from "moment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";

import { Button, LeadSenderAvatar, Select } from "../../components/ui";
import SenderLimitsSidebar, {
  DEFAULT_SENDER_DAILY_QUOTA_MAX,
  type SenderLimits,
} from "../../components/Campaigns/components/SenderLimitsSidebar";
import type { CampaignTableRow } from "./CampaignListing";
import { fetchOwner, fetchLiveAutomationActions } from "../../utils";
import axios from "../../utils/axios";
import { ownerSelector } from "../../store/store";
import { toast } from "react-toastify";
import EngagementAnalyticsGraph from "../../components/Dashboard/widgets/EngagementAnalyticsGraph";
import {
  buildEngagementAnalyticsChartData,
  engagementAnalyticsRowsToCsv,
  type EngagementAnalyticsRow,
} from "../../utils/engagementChart";
import {
  draftSearchFromCampaignId,
  hasLinkedCampaignLists,
  normalizeCampaignListIds,
} from "./campaignDraftFlow";
import { resolveCampaignTimezoneLabel } from "./campaignFlow";

const VIEW_STEPS = [
  "Campaign Performance",
  "LinkedIn senders",
  "Sender Performance",
  "Leads Analytics",
] as const;

const SELECT_LAST_30_DAYS = [
  { value: "last-30-days", label: "Last 30 days" },
  { value: "last-14-days", label: "Last 14 days" },
  { value: "last-7-days", label: "Last 7 days" },
  { value: "custom", label: "Custom range" },
];

type MetricCard = {
  title: string;
  value: string;
  delta: string;
};

type MetricGroup = "invitation" | "message" | "visit";

const ICON_BASE = "/icons/campaigns";

export default function CampaignViewDetails() {
  const { state } = useLocation();
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { owner, currentWorkspace, currentUser } = useSelector(ownerSelector);
  const fallbackCampaign = (state as { campaign?: CampaignTableRow } | null)?.campaign;

  const [campaignData, setCampaignData] = React.useState<any>(fallbackCampaign ?? null);
  const [senderData, setSenderData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [syncingList, setSyncingList] = React.useState(false);

  const campaignName = (campaignData?.name || campaignData?.campaignName || `Campaign ${campaignId || ""}`).trim();
  const [range, setRange] = React.useState("last-30-days");
  const [customStartDate, setCustomStartDate] = React.useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = React.useState<Date | null>(null);
  const [showCustomRangeCalendar, setShowCustomRangeCalendar] = React.useState(false);
  const [isSenderLimitsOpen, setIsSenderLimitsOpen] = React.useState(false);
  const [senderLimits, setSenderLimits] = React.useState<SenderLimits>({
    maxFollowsPerDay: 40,
    maxMessagesPerDay: 40,
    maxConnectionRequestsPerDay: 40,
    maxProfileViewsPerDay: 40,
  });
  const [savingSenderLimits, setSavingSenderLimits] = React.useState(false);

  const chartContainerRef = React.useRef<HTMLDivElement | null>(null);
  const customRangePopoverRef = React.useRef<HTMLDivElement | null>(null);
  const [downloadOption, setDownloadOption] = React.useState<string>("");
  const [engagementChartData, setEngagementChartData] = React.useState<EngagementAnalyticsRow[]>([]);
  const [liveActions, setLiveActions] = React.useState<
    Array<{
      id: string;
      action: string;
      leadName: string;
      status: string;
      time: string;
      error?: string;
    }>
  >([]);
  const [liveAutomationStatus, setLiveAutomationStatus] = React.useState("");

  const canEditDraft = String(campaignData?.campaignStatus || campaignData?.status || "").toUpperCase() === "DRAFT";
  const senderOwnerId = String(campaignData?.ownerId ?? "");

  React.useEffect(() => {
    if (!campaignId && !senderOwnerId) return undefined;
    let disposed = false;
    const load = async () => {
      try {
        const res = await fetchLiveAutomationActions({
          ownerId: senderOwnerId || undefined,
          campaignId: campaignId || undefined,
          limit: 30,
        });
        if (disposed) return;
        setLiveActions(Array.isArray(res?.actions) ? res.actions : []);
        setLiveAutomationStatus(String(res?.automationStatus || ""));
      } catch {
        if (!disposed) setLiveActions([]);
      }
    };
    load();
    const timer = window.setInterval(load, 5000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [campaignId, senderOwnerId]);

  const campaignStatusLabel = React.useMemo(() => {
    const normalized = String(campaignData?.campaignStatus || campaignData?.status || "")
      .trim()
      .toUpperCase();
    if (normalized === "ACTIVE") return "Running";
    if (normalized === "PAUSED") return "Paused";
    if (normalized === "DRAFT") return "Draft";
    if (normalized === "ARCHIVED") return "Archived";
    if (normalized === "COMPLETED") return "Finished";
    return normalized ? normalized.charAt(0) + normalized.slice(1).toLowerCase() : "—";
  }, [campaignData?.campaignStatus, campaignData?.status]);

  const safeProspectsWise = (campaignData?.prospectsWiseCampaignActions ?? []) as any[];
  const safeProspects = (campaignData?.prospects ?? []) as any[];

  const totalLeads = safeProspectsWise.length || safeProspects.length || 0;
  const excludedLeads = safeProspectsWise.filter((p) => !p.inCampaign).length;
  const inSequenceLeads = safeProspectsWise.filter((p) => p.inCampaign).length;

  // Done status for "progress" (kept simple/consistent with scheduler done states).
  const leadsCompleted = safeProspectsWise.filter((p) =>
    (p.campaignActions ?? []).some((a: any) => a.done === "DONE" || a.done === "HASREPLIED"),
  ).length;
  const progressPct = totalLeads > 0 ? Math.round((leadsCompleted / totalLeads) * 100) : 0;

  // Engagement analytics totals (used for the graph only).
  const connectionsSent = safeProspectsWise.filter((p) =>
    (p.campaignActions ?? []).some((a: any) => a.type === "invitation" && a.done === "DONE"),
  ).length;
  const connectionsAccepted = safeProspectsWise.filter((p) => p.connected === "ACCEPTED").length;
  const pendingConnections = safeProspectsWise.filter((p) => p.connected === "PENDING").length;
  const messageReplies = safeProspectsWise.filter((p) =>
    (p.campaignActions ?? []).some((a: any) => a.done === "HASREPLIED"),
  ).length;
  const messagesSent = safeProspectsWise.reduce((sum, p) => {
    const sentCount =
      (p.campaignActions ?? []).filter(
        (a: any) => (a.type === "message" || a.type === "messageWithDelay") && a.done === "DONE",
      ).length ?? 0;
    return sum + sentCount;
  }, 0);
  const profileVisits = safeProspectsWise.reduce((sum, p) => {
    const visitCount =
      (p.campaignActions ?? []).filter(
        (a: any) =>
          (a.type === "visit" || a.type === "visitWithDelay") && a.done === "DONE",
      ).length ?? 0;
    return sum + visitCount;
  }, 0);

  React.useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (range !== "custom") return;
      const container = customRangePopoverRef.current;
      if (!container) return;
      if (!container.contains(event.target as Node)) {
        setShowCustomRangeCalendar(false);
      }
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => document.removeEventListener("mousedown", onDocumentMouseDown);
  }, [range]);

  React.useEffect(() => {
    if (!campaignId) {
      setEngagementChartData([]);
      return;
    }
    let start: Date;
    let end: Date;
    if (range === "custom") {
      if (!customStartDate || !customEndDate) {
        // Keep previous chart until custom range is fully selected.
        return;
      }
      start = new Date(customStartDate);
      end = new Date(customEndDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    } else {
      const days = range === "last-30-days" ? 30 : range === "last-14-days" ? 14 : 7;
      end = new Date();
      end.setHours(23, 59, 59, 999);
      start = new Date(end);
      start.setDate(end.getDate() - (days - 1));
      start.setHours(0, 0, 0, 0);
    }

    (async () => {
      try {
        const workspaceId = currentWorkspace?._id as string | undefined;
        const userId = currentUser?.userId as string | undefined;
        const { data } = await axios.post("/campaign/getCampaignEngagementAnalytics", {
          ownerId: owner?._id,
          userId,
          workspaceId,
          campaignId,
          startDate: start.toISOString(),
          endDate: end.toISOString(),
        });
        if (Array.isArray(data?.rows)) {
          setEngagementChartData(data.rows);
          return;
        }
        setEngagementChartData([]);
      } catch (error) {
        console.log(error);
        const engagementRange: "week" | "month" = range === "last-7-days" ? "week" : "month";
        const fallback = buildEngagementAnalyticsChartData(engagementRange, {
          connectionsSent,
          connectionsAccepted,
          messagesSent,
          messageReplies,
          profileVisits,
          pendingConnections,
        }, {
          startDate: customStartDate,
          endDate: customEndDate,
        });
        setEngagementChartData(fallback);
      }
    })();
  }, [
    range,
    customStartDate,
    customEndDate,
    campaignId,
    owner?._id,
    currentWorkspace?._id,
    currentUser?.userId,
    connectionsSent,
    connectionsAccepted,
    messagesSent,
    messageReplies,
    profileVisits,
    pendingConnections,
  ]);

  const acceptanceRatePct = totalLeads > 0 ? Math.round((connectionsAccepted / totalLeads) * 100) : 0;
  const messagesSentPct = totalLeads > 0 ? Math.round((messagesSent / totalLeads) * 100) : 0;
  const profileVisitsInSelectedRange = React.useMemo(
    () =>
      (engagementChartData || []).reduce(
        (sum, row) => sum + Number(row?.profileVisits || 0),
        0
      ),
    [engagementChartData]
  );
  const profileVisitsPct =
    totalLeads > 0 ? Math.round((profileVisitsInSelectedRange / totalLeads) * 100) : 0;

  const orderedMetricGroups = React.useMemo(() => {
    const normalizeActionTypeToMetricGroup = (actionType: string): MetricGroup | null => {
      if (["invitation", "sendConnectionRequest"].includes(actionType)) return "invitation";
      if (["message", "messageWithDelay", "sendInMail"].includes(actionType)) return "message";
      if (["visit", "visitWithDelay", "viewProfile"].includes(actionType)) return "visit";
      return null;
    };

    const groups: MetricGroup[] = [];
    const seen = new Set<MetricGroup>();

    for (const action of (campaignData?.actions ?? []) as Array<{ type?: string }>) {
      const actionType = String(action?.type || "").trim();
      if (!actionType) continue;
      const group = normalizeActionTypeToMetricGroup(actionType);
      if (!group || seen.has(group)) continue;
      seen.add(group);
      groups.push(group);
    }

    return groups;
  }, [campaignData?.actions]);

  const METRICS: MetricCard[] = orderedMetricGroups.flatMap((group) => {
    if (group === "invitation") {
      return [
        {
          title: "Connection requests sent",
          value: String(connectionsSent),
          delta: `${totalLeads > 0 ? Math.round((connectionsSent / totalLeads) * 100) : 0}%`,
        },
        {
          title: "Connection acceptance rate",
          value: String(connectionsAccepted),
          delta: `${acceptanceRatePct}%`,
        },
      ];
    }

    if (group === "message") {
      return [{ title: "Messages sent", value: String(messagesSent), delta: `${messagesSentPct}%` }];
    }

    return [
      {
        title: "Profile visits",
        value: String(profileVisitsInSelectedRange),
        delta: `${profileVisitsPct}%`,
      },
    ];
  });

  const ACTIVE_DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
  const OWNER_SCHEDULE_DAY_ORDER = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ] as const;
  const dayFullToShort: Record<string, (typeof ACTIVE_DAY_SHORT)[number]> = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun",
  };

  /** Matches QuotaSettings: missing `active` defaults to on; only explicit `false` turns a day off. */
  const isOwnerDayActive = (period: unknown) => (period as { active?: boolean } | null | undefined)?.active !== false;

  const activeDays = React.useMemo(() => {
    const days = (campaignData?.schedule?.days ?? []) as string[];
    if (Array.isArray(days) && days.length > 0) {
      return days.map((d) => dayFullToShort[d] ?? d).filter(Boolean).slice(0, 7);
    }

    // OWNER_DEFAULT / legacy: campaign.schedule.days is empty; use sender working hours (owner.timePeriods).
    if (senderOwnerId && !senderData && loading) {
      return [];
    }

    const periods =
      senderData?.timePeriods ??
      (String(owner?._id ?? "") === senderOwnerId && senderOwnerId ? owner?.timePeriods : undefined) ??
      {};

    const ownerActiveFull = OWNER_SCHEDULE_DAY_ORDER.filter((day) => isOwnerDayActive((periods as Record<string, unknown>)[day]));

    if (ownerActiveFull.length === 0) {
      return [];
    }

    return ownerActiveFull.map((d) => dayFullToShort[d] ?? d).filter(Boolean);
  }, [
    campaignData?.schedule?.days,
    senderData,
    senderData?.timePeriods,
    senderOwnerId,
    loading,
    owner?._id,
    owner?.timePeriods,
  ]);

  const formatDisplayDateTime = (value: any) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const combineScheduleDateAndTime = (dateValue?: string, timeValue?: string) => {
    if (!dateValue) return "—";
    const dateMoment = moment(dateValue);
    if (!dateMoment.isValid()) return "—";

    if (!timeValue) return formatDisplayDateTime(dateMoment.toDate());

    const parsedTime = moment(timeValue, "HH:mm", true);
    if (!parsedTime.isValid()) return formatDisplayDateTime(dateMoment.toDate());

    return formatDisplayDateTime(
      dateMoment
        .clone()
        .hour(parsedTime.hour())
        .minute(parsedTime.minute())
        .second(0)
        .millisecond(0)
        .toDate()
    );
  };

  const handleDownloadChartSvg = () => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) return;

    const svgElement = chartContainer.querySelector("svg");
    if (!svgElement) {
      toast.error("Unable to download chart right now.");
      return;
    }

    const serializer = new XMLSerializer();
    const svgContent = serializer.serializeToString(svgElement);
    const blob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "engagement-analytics.svg";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    setDownloadOption("");
  };

  const handleDownloadChartPng = () => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) return;

    const svgElement = chartContainer.querySelector("svg");
    if (!svgElement) {
      toast.error("Unable to download chart right now.");
      return;
    }

    const serializer = new XMLSerializer();
    let svgContent = serializer.serializeToString(svgElement);
    if (!svgContent.includes("xmlns=")) {
      svgContent = svgContent.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
    }

    const svgBlob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.width || 1200;
      canvas.height = image.height || 400;
      const context = canvas.getContext("2d");
      if (!context) {
        URL.revokeObjectURL(svgUrl);
        toast.error("Unable to prepare PNG export.");
        return;
      }

      const cssWhite =
        window.getComputedStyle(document.documentElement).getPropertyValue("--color-white").trim() || "white";
      context.fillStyle = cssWhite;
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);
      URL.revokeObjectURL(svgUrl);

      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error("Unable to generate PNG file.");
          return;
        }
        const pngUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = pngUrl;
        anchor.download = "engagement-analytics.png";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(pngUrl);
      }, "image/png");
    };

    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      toast.error("Unable to convert chart to PNG.");
    };

    image.src = svgUrl;
    setDownloadOption("");
  };

  const handleDownloadChartCsv = () => {
    if (!engagementChartData.length) {
      toast.error("No data to export.");
      return;
    }
    const csv = engagementAnalyticsRowsToCsv(engagementChartData);
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "engagement-analytics.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    setDownloadOption("");
  };

  React.useEffect(() => {
    if (!senderData) return;

    const dailyQuota = senderData?.dailyQuota ?? {};

    setSenderLimits({
      maxFollowsPerDay: Number(dailyQuota?.follows ?? 0),
      maxMessagesPerDay: Number(dailyQuota?.messages ?? 0),
      maxConnectionRequestsPerDay: Number(dailyQuota?.invitations ?? 0),
      maxProfileViewsPerDay: Number(dailyQuota?.visits ?? 0),
    });
  }, [senderData]);

  const handleSaveSenderLimits = async () => {
    if (!canEditDraft) return;
    if (!senderOwnerId) {
      toast.error("Unable to resolve sender");
      return;
    }

    setSavingSenderLimits(true);
    try {
      await axios.post("/owner/updateDailyQuota", {
        ownerId: senderOwnerId,
        dailyQuota: {
          follows: Number(senderLimits.maxFollowsPerDay || 0),
          invitations: Number(senderLimits.maxConnectionRequestsPerDay || 0),
          messages: Number(senderLimits.maxMessagesPerDay || 0),
          visits: Number(senderLimits.maxProfileViewsPerDay || 0),
        },
      });

      toast.success("Sender limits updated successfully");
      const updatedOwner = await fetchOwner(senderOwnerId);
      setSenderData(updatedOwner ?? null);
      setIsSenderLimitsOpen(false);
    } catch (error) {
      console.log(error);
      toast.error("Unable to update sender limits");
    } finally {
      setSavingSenderLimits(false);
    }
  };

  const loadCampaignDetails = React.useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);

    try {
      const workspaceId = currentWorkspace?._id as string | undefined;
      const userId = currentUser?.userId as string | undefined;

      const response = await axios.post("/campaign/getCampaignData", {
        ownerId: owner?._id,
        userId,
        workspaceId,
        campaignId,
      });

      setCampaignData(response?.data?.campaign ?? null);

      const ownerId = response?.data?.campaign?.ownerId;
      if (ownerId) {
        const loadedOwner = await fetchOwner(ownerId);
        setSenderData(loadedOwner ?? null);
      }
    } catch (e) {
      toast.error("Error fetching campaign details");
    } finally {
      setLoading(false);
    }
  }, [campaignId, currentUser?.userId, currentWorkspace?._id, owner?._id]);

  React.useEffect(() => {
    void loadCampaignDetails();
  }, [loadCampaignDetails]);

  const senderName = senderData?.name || senderData?.firstName || senderData?.email || "Sender";
  const senderAvatarSrc = senderData?.picture || senderData?.profilePic || senderData?.profileImage || "";

  const handleEdit = () => {
    if (!campaignId) return;
    navigate(
      {
        pathname: "/campaign-management/create",
        search: draftSearchFromCampaignId(campaignId),
      },
      { state: { campaignId } },
    );
  };

  const linkedListIds = normalizeCampaignListIds(
    campaignData?.listIds,
    campaignData?.listId?._id || campaignData?.listId
  );
  const hasLinkedLists = hasLinkedCampaignLists(linkedListIds);

  const handleSyncList = async () => {
    if (!campaignId || !owner?._id) {
      toast.error("Missing campaign context.");
      return;
    }
    if (!hasLinkedLists) {
      toast.error("This campaign has no linked lists.");
      return;
    }

    const workspaceId = currentWorkspace?._id as string | undefined;
    const userId = currentUser?.userId as string | undefined;
    setSyncingList(true);
    const loader = toast.loading("Syncing lists...");

    try {
      const response = await axios.post("/campaign/syncCampaignList", {
        ownerId: owner._id,
        userId,
        workspaceId,
        campaignId,
      });
      const addedCount = Number(response?.data?.addedCount || 0);
      const message =
        response?.data?.message ||
        (addedCount > 0
          ? `Added ${addedCount} prospect(s) from linked lists.`
          : "Lists are already in sync.");

      toast.update(loader, {
        render: message,
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });

      if (addedCount > 0) {
        await loadCampaignDetails();
      }
    } catch (err: any) {
      const serverError =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to sync lists.";
      toast.update(loader, {
        render: serverError,
        type: "error",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
    } finally {
      setSyncingList(false);
    }
  };

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden bg-background-grey px-4 pb-4 sm:px-6 sm:pb-6">
      <header className="shrink-0 py-6 pt-8">
        <h1 className="text-xl font-semibold text-dark-black">View Campaign</h1>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[8px] border border-light-grey bg-white">
        <div className="overflow-x-auto border-b border-light-grey px-4 py-4 sm:px-6 sm:py-5">
          <ViewCampaignStepper currentStep={0} />
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          <div className="rounded-[8px] border border-light-grey bg-white p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 text-base capitalize font-semibold text-dark-black truncate">
               {campaignName}
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-light-grey bg-white text-dark-black"
                  onClick={() => void handleSyncList()}
                  isDisabled={syncingList || loading}
                >
                  {syncingList ? "Syncing..." : "Sync lists"}
                </Button>
                {canEditDraft ? (
                  <Button
                    variant="primary"
                    size="sm"
                    className="h-8 "
                    iconSrc={`${ICON_BASE}/pencil.svg`}
                    iconClassName="!h-4 !w-4"
                    showAfter
                    onClick={handleEdit}
                  >
                    Edit Campaign
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[260px_1fr]">
              <aside className="space-y-3">
                <div className="rounded-[8px] border border-light-grey">
                  <div className="border-b border-light-grey px-3 py-2 text-xs font-semibold uppercase text-dark-black">
                    Campaign Progress
                  </div>
                  <div className="p-3">
                    <div>
                      <div className="mb-1 flex items-center justify-between text-xs text-dark-grey">
                        <span>Progress</span>
                        <span className="font-semibold text-dark-black">{progressPct}%</span>
                      </div>
                      <div className="h-6 rounded-[8px] bg-light-grey px-2 py-1 text-center text-xs text-dark-grey">
                        {totalLeads === 0 || leadsCompleted === 0 ? "No progress yet" : `${progressPct}% complete`}
                      </div>
                      <p className="mt-2 text-xs text-dark-grey">
                        {leadsCompleted} of {totalLeads} leads completed
                      </p>
                    </div>

                    <div className="mt-3 divide-y divide-light-grey rounded-[8px] border border-light-grey bg-white">
                      <ProgressStat
                        icon={`${ICON_BASE}/status.svg`}
                        label="Status"
                        value={campaignStatusLabel}
                        tone="pill"
                      />
                      <ProgressStat
                        icon={`${ICON_BASE}/calender.svg`}
                        label="Starts on"
                        value={combineScheduleDateAndTime(
                          campaignData?.schedule?.startDate,
                          campaignData?.schedule?.from
                        )}
                      />
                      <ProgressStat
                        icon={`${ICON_BASE}/calender.svg`}
                        label="Ends on"
                        value={combineScheduleDateAndTime(
                          campaignData?.schedule?.endDate,
                          campaignData?.schedule?.to
                        )}
                      />
                      <ProgressStat
                        icon={`${ICON_BASE}/calender.svg`}
                        label="Timezone"
                        value={resolveCampaignTimezoneLabel(campaignData?.schedule?.timezone)}
                      />
                      <ProgressStat
                        icon={`${ICON_BASE}/persons.svg`}
                        label="Total leads"
                        value={String(totalLeads)}
                      />
                      {/* Excluded leads not finalized on backend yet */}
                      {/* <ProgressStat
                        icon={`${ICON_BASE}/person_excluded.svg`}
                        label="Excluded leads"
                        value={String(excludedLeads)}
                      /> */}
                      <ProgressStat icon={`${ICON_BASE}/sequence.svg`} label="In the sequence" value={String(inSequenceLeads)} />
                      {/* These statuses are not wired to backend metrics yet */}
                      {/* <ProgressStat icon={`${ICON_BASE}/pending.svg`} label="Pending" value="0" /> */}
                      {/* <ProgressStat icon={`${ICON_BASE}/paused.svg`} label="Paused" value="0" /> */}
                      {/* <ProgressStat icon={`${ICON_BASE}/failed.svg`} label="Failed" value="0" /> */}
                      {/* <ProgressStat icon={`${ICON_BASE}/finished.svg`} label="Finished" value="0" /> */}
                    </div>
                  </div>
                </div>

                <div className="rounded-[8px] border border-light-grey p-3">
                  <p className="mb-2 text-xs font-semibold uppercase text-dark-black">Active Days</p>
                  <div className="flex flex-wrap gap-2">
                    {activeDays.length === 0 ? (
                      <span className="text-xs text-dark-black">—</span>
                    ) : (
                      activeDays.map((day) => (
                        <span
                          key={day}
                          className="rounded-full border border-brand-blue bg-bg-blue px-2.5 py-1 text-xs text-brand-blue"
                        >
                          {day}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                <div className="rounded-[8px] border border-light-grey p-3">
                  <p className="mb-2 text-xs font-semibold uppercase text-dark-black">Linkedin Senders</p>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <LeadSenderAvatar picture={senderAvatarSrc} className="h-8 w-8" />
                      <span className="text-sm text-dark-black">{senderName}</span>
                    </div>
                    <button
                      type="button"
                      aria-label="Open sender limits"
                      onClick={() => setIsSenderLimitsOpen(true)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] hover:bg-background-grey"
                    >
                      <img src="/icons/setting.svg" alt="" className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </aside>

              <section className="rounded-[8px] border border-light-grey p-3">
                <div className="mb-3">
                  <h2 className="text-sm font-semibold uppercase text-dark-black">Engagement Analytics</h2>
                  <div
                    className="relative mt-2 flex gap-2"
                    ref={customRangePopoverRef}
                    onMouseDownCapture={() => {
                      if (range === "custom") setShowCustomRangeCalendar(true);
                    }}
                  >
                    <Select
                      ariaLabel="Time range"
                      options={SELECT_LAST_30_DAYS}
                      value={range}
                      onChange={(nextRange) => {
                        setRange(nextRange);
                        setShowCustomRangeCalendar(nextRange === "custom");
                      }}
                      size="sm"
                      variant="compact"
                    />
                    {range === "custom" && showCustomRangeCalendar ? (
                      <div className="absolute z-40 mt-10 rounded-[8px] border border-light-grey bg-white p-2 shadow-lg">
                        <div className="w-full md:max-w-[360px]">
                          <DatePicker
                            selected={customStartDate}
                            onChange={(dates) => {
                              const [start, end] = (dates as [Date | null, Date | null]) ?? [null, null];
                              setCustomStartDate(start);
                              setCustomEndDate(end);
                              if (start && end) setShowCustomRangeCalendar(false);
                            }}
                            startDate={customStartDate}
                            endDate={customEndDate}
                            selectsRange
                            inline
                            calendarClassName="ls-datepicker-calendar"
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  {METRICS.map((item) => (
                    <div key={item.title} className="rounded-[8px] border border-light-grey px-3 py-2">
                      <p className="text-xs text-dark-grey">{item.title}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-sm font-semibold text-dark-black">{item.value}</span>
                        <span className="text-xs text-success">{item.delta}</span>
                      </div>
                      <p className="mt-1 text-[11px] text-dark-grey">From {totalLeads} prospects in campaign</p>
                    </div>
                  ))}
                </div>

                <div className="mt-3 rounded-[8px] border border-light-grey p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-dark-black">Live actions</h3>
                    {liveAutomationStatus ? (
                      <span className="text-[11px] text-dark-grey">{liveAutomationStatus.replaceAll("_", " ")}</span>
                    ) : null}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-xs">
                      <thead>
                        <tr className="text-dark-grey">
                          <th className="pb-2 font-medium">Action</th>
                          <th className="pb-2 font-medium">Status</th>
                          <th className="pb-2 font-medium">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {liveActions.length === 0 ? (
                          <tr>
                            <td className="py-2 text-dark-grey" colSpan={3}>
                              Actions will appear here as the campaign runs.
                            </td>
                          </tr>
                        ) : (
                          liveActions.map((row) => (
                            <tr key={row.id} className="border-t border-light-grey">
                              <td className="py-2 text-dark-black">
                                {row.action}
                                {row.leadName ? ` — ${row.leadName}` : ""}
                              </td>
                              <td className="py-2 capitalize text-dark-black">{row.status.replaceAll("_", " ")}</td>
                              <td className="py-2 text-dark-grey">
                                {row.time ? new Date(row.time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-3 rounded-[8px] border border-light-grey p-2">
                  <div className="mb-2 flex justify-end">
                    <Select
                      ariaLabel="Download format"
                      options={[
                        { value: "svg", label: "Download SVG" },
                        { value: "png", label: "Download PNG" },
                        { value: "csv", label: "Download CSV" },
                      ]}
                      value={downloadOption}
                      onChange={(value) => {
                        if (value === "svg") handleDownloadChartSvg();
                        if (value === "png") handleDownloadChartPng();
                        if (value === "csv") handleDownloadChartCsv();
                        setDownloadOption("");
                      }}
                      placeholder="Download"
                      size="sm"
                      variant="compact"
                    />
                  </div>
                  <EngagementAnalyticsGraph chartData={engagementChartData} containerRef={chartContainerRef} />
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <SenderLimitsSidebar
        isOpen={isSenderLimitsOpen}
        onClose={() => setIsSenderLimitsOpen(false)}
        senderName={senderName}
        senderAvatarSrc={senderAvatarSrc}
        linkedInAvatarVariant="sender"
        value={senderLimits}
        max={DEFAULT_SENDER_DAILY_QUOTA_MAX}
        onChange={setSenderLimits}
        readOnly={!canEditDraft}
        onSave={canEditDraft && !savingSenderLimits ? handleSaveSenderLimits : undefined}
      />
    </section>
  );
}

function ViewCampaignStepper({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex w-full items-center">
      {VIEW_STEPS.map((label, index) => {
        const isCurrent = index === currentStep;
        const isCompleted = index > currentStep;
        return (
          <React.Fragment key={label}>
            <div className="inline-flex items-center gap-3">
              <span
                className={[
                  "inline-flex h-8 w-8 items-center justify-center rounded-full border text-md font-medium",
                  isCurrent || isCompleted
                    ? "border-bright-green bg-bright-green text-dark-black"
                    : "border-light-grey bg-medium-grey text-dark-grey",
                ].join(" ")}
              >
                {isCompleted ? <img src={`/icons/correct.svg`} alt="" className="!h-4 !w-4" /> : index + 1}
              </span>
              <span className="hidden whitespace-nowrap text-sm font-medium text-dark-black sm:inline">{label}</span>
            </div>
            {index < VIEW_STEPS.length - 1 ? (
              <div className="mx-1 h-px min-w-4 flex-1 bg-light-grey sm:mx-4 sm:min-w-10" aria-hidden="true" />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ProgressStat({
  icon,
  label,
  value,
  tone = "default",
}: {
  icon: string;
  label: string;
  value: string;
  tone?: "default" | "pill";
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-3.5 text-xs">
      <div className="min-w-0 flex items-center gap-2 text-dark-grey">
        <img src={icon} alt="" className="h-[14px] w-[14px]" />
        <span>{label}</span>
      </div>
      {tone === "pill" ? (
        <span className="rounded-full bg-bg-blue px-2 py-0.5 text-[11px] text-brand-blue">{value}</span>
      ) : (
        <span className="max-w-[110px] text-right font-medium leading-tight break-words text-dark-black">
          {value}
        </span>
      )}
    </div>
  );
}
